import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertTriangle, Clock, MapPin, UserX, CheckCircle, X, RefreshCw,
  Shield, Eye, Filter, Plus, ChevronDown, Bell, Users, PhoneCall, HelpCircle, Info
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { AvertissementsRichBlock } from "@/components/bonus/AvertissementsRichBlock";

const TYPE_CONFIG_DEFAULT = {
  retard: { label: "Retard", icon: Clock, color: "bg-amber-100 text-amber-700 border-amber-300" },
  geo_hors_zone: { label: "Géoloc hors zone", icon: MapPin, color: "bg-red-100 text-red-700 border-red-300" },
  absence_non_justifiee: { label: "Absence injustifiée", icon: UserX, color: "bg-red-100 text-red-700 border-red-300" },
  oubli_punch: { label: "Oubli de punch", icon: Clock, color: "bg-orange-100 text-orange-700 border-orange-300" },
  port_epi_non_conforme: { label: "Port EPI non conforme", icon: AlertTriangle, color: "bg-red-100 text-red-700 border-red-300" },
  vetements_non_conformes: { label: "Vêtements non conformes", icon: AlertTriangle, color: "bg-orange-100 text-orange-700 border-orange-300" },
  cigarette: { label: "Cigarette", icon: AlertTriangle, color: "bg-amber-100 text-amber-700 border-amber-300" },
  pause_non_respectee: { label: "Pause non respectée", icon: AlertTriangle, color: "bg-amber-100 text-amber-700 border-amber-300" },
  mesures_securite_non_conformes: { label: "Mesures sécurité non conformes", icon: Shield, color: "bg-red-100 text-red-700 border-red-300" },
  attitude: { label: "Attitude", icon: AlertTriangle, color: "bg-orange-100 text-orange-700 border-orange-300" },
  autre: { label: "Autre", icon: AlertTriangle, color: "bg-gray-100 text-gray-700 border-gray-300" },
};

export const GestionAvertissements = () => {
  const [pendingWarnings, setPendingWarnings] = useState([]);
  const [officiels, setOfficiels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [classifData, setClassifData] = useState({ type_officiel: "", notes: "", envoyer_notification: true });
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreRaison, setIgnoreRaison] = useState("");
  const [manuelOpen, setManuelOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);  // 🆕 [Phase 50] Dialog "Comprendre"
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  // 🆕 [Phase 69 — 2026-06-18] Liste évolutive des catégories perso saisies sous "Autre"
  const [categoriesPerso, setCategoriesPerso] = useState([]);
  const [manuel, setManuel] = useState({ employee_id: "", type: "retard", description: "", date_incident: new Date().toISOString().slice(0, 10), notes: "", categorie_personnalisee: "" });
  const [activeTab, setActiveTab] = useState("pending");
  const [filterType, setFilterType] = useState("all");
  // Absence alerts state
  const [absencePreview, setAbsencePreview] = useState(null);
  const [absenceLoading, setAbsenceLoading] = useState(false);

  // Filtrage des types par rôle de l'employé
  const getTypesForEmployee = (employeeId) => {
    if (!employeeId) return typesOptions;
    const emp = employees.find(e => e.id === employeeId);
    if (!emp) return typesOptions;
    const role = emp.type_employe || emp.role || "terrain";
    
    // Types pertinents par type d'employé
    const TYPES_PAR_ROLE = {
      terrain: ["retard", "oubli_punch", "port_epi_non_conforme", "vetements_non_conformes", 
                "cigarette", "pause_non_respectee", "mesures_securite_non_conformes", 
                "attitude", "absence_non_justifiee", "geo_hors_zone", "autre"],
      admin: ["retard", "oubli_punch", "absence_non_justifiee", "attitude", "autre"],
      representant: ["retard", "absence_non_justifiee", "attitude", "autre"],
      charge_projet: ["cp_gestion_production", "cp_structure", "cp_horaires_social", "attitude", "autre"],
    };
    const allowed = TYPES_PAR_ROLE[role] || TYPES_PAR_ROLE.terrain;
    return typesOptions.filter(t => allowed.includes(t.value));
  };

  const deleteWarning = async (warningId) => {
    if (!window.confirm("Supprimer définitivement cet avertissement ?")) return;
    try {
      await axios.delete(`${api.baseUrl}/avertissements-auto/pending/${warningId}`);
      toast.success("Avertissement supprimé");
      loadData();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression");
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, officielsRes, statsRes, empRes, catsRes, persoRes] = await Promise.all([
        axios.get(`${api.baseUrl}/avertissements-auto/pending?statut=en_attente`),
        axios.get(`${api.baseUrl}/avertissements-auto/pending?statut=classe`),
        axios.get(`${api.baseUrl}/avertissements-auto/stats`),
        axios.get(`${api.baseUrl}/employees`),
        axios.get(`${api.baseUrl}/avertissements-auto/categories/config`),
        axios.get(`${api.baseUrl}/avertissements-auto/categories-personnalisees`),
      ]);
      const pending = pendingRes.data;
      const off = officielsRes.data;
      const s = statsRes.data;
      const emp = empRes.data;
      const cats = catsRes.data;
      const perso = persoRes.data;
      setPendingWarnings(pending.pending_warnings || []);
      setOfficiels(off.pending_warnings || []);
      setStats(s);
      setEmployees(emp.employees || emp || []);
      setCategories(cats.configs || []);
      setCategoriesPerso(perso.categories || []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAbsencePreview = async () => {
    setAbsenceLoading(true);
    try {
      const res = await axios.post(`${api.baseUrl}/punch/test-absence-alert`);
      if (res.status !== 200) throw new Error("Erreur");
      const data = res.data;
      setAbsencePreview(data);
    } catch (e) {
      toast.error("Erreur chargement aperçu absences");
    } finally {
      setAbsenceLoading(false);
    }
  };
  const openClassify = (w) => {
    setSelected(w);
    setClassifData({ type_officiel: w.type_suggere, notes: "", envoyer_notification: true });
    setDialogOpen(true);
  };

  const openIgnore = (w) => {
    setSelected(w);
    setIgnoreRaison("");
    setIgnoreOpen(true);
  };

  const handleClassify = async () => {
    if (!selected) return;
    try {
      const res = await axios.post(`${api.baseUrl}/avertissements-auto/pending/${selected.id}/classifier`, { ...classifData, classifie_par: "Manager" });
      const data = res.data;
      if (res.status === 200) {
        toast.success(`✅ Avertissement officiel créé (${data.count_total_type}/${data.seuil})`);
        if (data.rencontre_requise) {
          toast.warning("⚠️ Seuil atteint — rencontre avec la direction requise!");
        }
        setDialogOpen(false);
        loadData();
      } else {
        toast.error(data.detail || "Erreur classification");
      }
    } catch (e) {
      toast.error("Erreur lors de la classification");
    }
  };

  const handleIgnore = async () => {
    if (!selected) return;
    try {
      await axios.post(`${api.baseUrl}/avertissements-auto/pending/${selected.id}/ignorer`, { raison: ignoreRaison, par: "Manager" });
      toast.success("Avertissement ignoré");
      setIgnoreOpen(false);
      loadData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleCreerManuel = async () => {
    try {
      // 🆕 [Phase 69] Si type=autre, on persiste aussi la catégorie perso
      const payload = { ...manuel, emis_par: "Manager" };
      if (manuel.type !== "autre") {
        delete payload.categorie_personnalisee;
      }
      const res = await axios.post(`${api.baseUrl}/avertissements-auto/creer-manuel`, payload);
      const data = res.data;
      if (res.status === 200) {
        toast.success("Avertissement créé");
        if (data.rencontre_requise) toast.warning("⚠️ Seuil atteint!");
        setManuelOpen(false);
        // Reset form
        setManuel({ employee_id: "", type: "retard", description: "", date_incident: new Date().toISOString().slice(0, 10), notes: "", categorie_personnalisee: "" });
        loadData();
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const filteredPending = filterType === "all"
    ? pendingWarnings
    : pendingWarnings.filter(w => w.type_suggere === filterType);

  // Build dynamic type options from loaded categories
  const typesOptions = categories.length > 0
    ? categories.map(c => ({ value: c.type, label: c.label }))
    : Object.entries(TYPE_CONFIG_DEFAULT).map(([k, v]) => ({ value: k, label: v.label }));

  const TypeBadge = ({ type, categoriePerso }) => {
    // 🆕 [Phase 69] Si type="autre" + categorie_personnalisee dispo → affiche-la à la place
    const catFromDB = categories.find(c => c.type === type);
    let label;
    if (type === "autre" && categoriePerso) {
      label = categoriePerso;
    } else {
      label = catFromDB?.label || TYPE_CONFIG_DEFAULT[type]?.label || type;
    }
    const cfg = TYPE_CONFIG_DEFAULT[type] || { icon: AlertTriangle, color: "bg-gray-100 text-gray-700" };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Gestion des Avertissements</h1>
          <p className="text-slate-500 mt-1 text-sm">Classifiez les avertissements automatiques</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setInfoOpen(true)} data-testid="btn-info-avertissements" title="Comprendre les avertissements">
            <Info className="w-4 h-4 mr-1" /> Comprendre
          </Button>
          <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className="w-4 h-4 mr-1" /> Actualiser</Button>
          <Button size="sm" onClick={() => setManuelOpen(true)}><Plus className="w-4 h-4 mr-1" /> Manuel</Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-700">{stats.pending_count}</div>
              <div className="text-sm text-amber-600">En attente de classification</div>
            </CardContent>
          </Card>
          {Object.entries(stats.stats_by_type || {}).map(([key, val]) => (
            <Card key={key}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-slate-800">{val.count}</div>
                <div className="text-sm text-slate-500">{val.label}</div>
                <div className="text-xs text-slate-400 mt-1">Seuil: {val.seuil_rencontre}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            En attente
            {pendingWarnings.length > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">{pendingWarnings.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="officiel">Classifiés</TabsTrigger>
          <TabsTrigger value="absences">
            <Bell className="w-3.5 h-3.5 mr-1" />
            Alertes Présence
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {/* Filtre */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button variant={filterType === "all" ? "default" : "outline"} size="sm" onClick={() => setFilterType("all")}>Tous</Button>
            {typesOptions.filter(t => categories.find(c => c.type === t.value && c.categorie_type === "auto")).map(t => (
              <Button key={t.value} variant={filterType === t.value ? "default" : "outline"} size="sm" onClick={() => setFilterType(t.value)}>{t.label}</Button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : filteredPending.length === 0 ? (
            <Card><CardContent className="pt-6 text-center text-slate-500"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />Aucun avertissement en attente</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filteredPending.map(w => (
                <Card key={w.id} className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeBadge type={w.type_suggere} />
                          <EmployeeAvatar photoUrl={w.employee_photo} prenom={(w.employee_nom || '?').split(' ')[0]} nom={(w.employee_nom || '').split(' ').slice(1).join(' ')} size="xs" />
                          <span className="font-semibold text-slate-800">{w.employee_nom}</span>
                          <span className="text-xs text-slate-400">{w.date_incident}</span>
                        </div>
                        <p className="text-sm text-slate-600">{w.description}</p>
                        {w.distance_metres && (
                          <p className="text-xs text-slate-400 mt-1">Distance: {Math.round(w.distance_metres)}m</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-500">Avertissements officiels ce type (saison): {w.count_officiel}/{w.seuil_rencontre}</span>
                          {w.count_officiel >= w.seuil_rencontre - 1 && (
                            <Badge variant="destructive" className="text-xs">⚠️ Proche du seuil</Badge>
                          )}
                          {w.punch_id && (
                            <a
                              href={`/validation-heures?punch_id=${w.punch_id}`}
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                              data-testid={`lien-punch-${w.id}`}
                            >
                              <MapPin className="w-3 h-3" /> Voir le pointage
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0 mt-2 sm:mt-0">
                        <Button size="sm" variant="default" onClick={() => openClassify(w)}>
                          <Shield className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Classifier</span>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openIgnore(w)}>
                          <X className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Ignorer</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="officiel" className="mt-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : officiels.length === 0 ? (
            <Card><CardContent className="pt-6 text-center text-slate-500">Aucun avertissement classifié</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {officiels.map(w => (
                <Card key={w.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeBadge type={w.type_officiel || w.type_suggere} categoriePerso={w.categorie_personnalisee} />
                          <span className="font-semibold">{w.employee_nom}</span>
                          <span className="text-xs text-slate-400">{w.date_incident}</span>
                          <Badge variant="outline" className="text-xs text-green-600 border-green-300">Classifié par {w.classifie_par}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{w.description}</p>
                        {w.notes_classification && <p className="text-xs text-slate-400 mt-1">Notes: {w.notes_classification}</p>}
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => deleteWarning(w.id)}
                        title="Supprimer cet avertissement"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab Alertes Présence */}
        <TabsContent value="absences" className="mt-4">
          <div className="space-y-4">
            {/* Explications */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900 text-sm">Alertes automatiques de présence</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Chaque jour de semaine, le système envoie des SMS automatiques aux chefs d'équipe:
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-xs text-blue-700">
                        <span className="w-12 font-mono bg-white rounded px-1 py-0.5 border border-blue-200 text-center">7h30</span>
                        <span>⚠️ Alerte retard — SMS au chargé de projet si un employé n'est pas pointé</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-blue-700">
                        <span className="w-12 font-mono bg-white rounded px-1 py-0.5 border border-blue-200 text-center">9h00</span>
                        <span>🚨 Alerte absence — SMS d'escalade au chargé de projet si toujours non pointé (2h)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bouton tester */}
            <div className="flex items-center gap-3">
              <Button
                onClick={loadAbsencePreview}
                disabled={absenceLoading}
                variant="outline"
                data-testid="btn-test-absence-alert"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${absenceLoading ? "animate-spin" : ""}`} />
                Voir l'état actuel des présences
              </Button>
              {absencePreview && (
                <span className="text-xs text-slate-500">
                  Mis à jour: {new Date().toLocaleTimeString("fr-CA")}
                </span>
              )}
            </div>

            {/* Aperçu */}
            {absencePreview && (
              <div className="space-y-3">
                {/* Résumé */}
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-slate-800">{absencePreview.bons_actifs}</p>
                      <p className="text-xs text-slate-500">Chantiers actifs aujourd'hui</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{absencePreview.employes_punches}</p>
                      <p className="text-xs text-slate-500">Employés pointés</p>
                    </CardContent>
                  </Card>
                  <Card className={absencePreview.chefs_a_alerter > 0 ? "border-red-200" : "border-green-200"}>
                    <CardContent className="pt-4 text-center">
                      <p className={`text-2xl font-bold ${absencePreview.chefs_a_alerter > 0 ? "text-red-600" : "text-green-600"}`}>
                        {absencePreview.chefs_a_alerter}
                      </p>
                      <p className="text-xs text-slate-500">Chargé(s) de projet à alerter</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Détail par chef */}
                {absencePreview.preview && absencePreview.preview.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Équipes avec absences</p>
                    {absencePreview.preview.map((item, i) => (
                      <Card key={item.id || item._id || i} className="border-l-4 border-l-red-400">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Users className="w-4 h-4 text-slate-600" />
                                <span className="font-semibold text-slate-800">{item.chef}</span>
                                {item.telephone && (
                                  <span className="text-xs text-slate-400 flex items-center gap-0.5">
                                    <PhoneCall className="w-3 h-3" /> {item.telephone}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mb-2">📍 Chantier: {item.client_nom || "—"}</p>
                              <div className="space-y-1">
                                {item.non_punched_names.map((nom, j) => (
                                  <div key={j} className="flex items-center gap-2 text-sm">
                                    <span className="w-2 h-2 bg-red-400 rounded-full" />
                                    <span className="text-slate-700">{nom}</span>
                                    <Badge variant="outline" className="text-xs text-red-600 border-red-200">Non pointé</Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <Badge variant="destructive" className="text-xs">
                                {item.non_punched_count} absent{item.non_punched_count > 1 ? "s" : ""}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Tous les employés assignés aujourd'hui sont pointés!</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {!absencePreview && !absenceLoading && (
              <Card>
                <CardContent className="pt-6 text-center text-slate-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Cliquez "Voir l'état actuel" pour afficher les présences du jour</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Classification */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Classifier l'avertissement</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium">{selected.employee_nom}</p>
                <p className="text-sm text-slate-600">{selected.description}</p>
                <p className="text-xs text-slate-400 mt-1">{selected.date_incident}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Type d'avertissement officiel</label>
                <Select value={classifData.type_officiel} onValueChange={v => setClassifData(p => ({ ...p, type_officiel: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {typesOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Notes (optionnel)</label>
                <Textarea
                  className="mt-1"
                  value={classifData.notes}
                  onChange={e => setClassifData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Contexte, explications..."
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notif"
                  checked={classifData.envoyer_notification}
                  onChange={e => setClassifData(p => ({ ...p, envoyer_notification: e.target.checked }))}
                />
                <label htmlFor="notif" className="text-sm text-slate-700">Envoyer notification email à l'employé</label>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                ⚠️ Avertissements officiels actuels (ce type, cette saison): <strong>{selected.count_officiel}/{selected.seuil_rencontre}</strong>
                {selected.count_officiel + 1 >= selected.seuil_rencontre && <p className="mt-1 font-medium">En classifiant, le seuil sera atteint → rencontre avec la direction requise!</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleClassify} disabled={!classifData.type_officiel}>
              <Shield className="w-4 h-4 mr-1" /> Rendre officiel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ignorer */}
      <Dialog open={ignoreOpen} onOpenChange={setIgnoreOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ignorer cet avertissement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Cet avertissement ne sera pas enregistré officiellement.</p>
            <div>
              <label className="text-sm font-medium">Raison (optionnel)</label>
              <Textarea className="mt-1" value={ignoreRaison} onChange={e => setIgnoreRaison(e.target.value)} placeholder="Fausse alarme, contexte particulier..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIgnoreOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleIgnore}><X className="w-4 h-4 mr-1" /> Confirmer ignorer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Avertissement Manuel */}
      <Dialog open={manuelOpen} onOpenChange={setManuelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Créer un avertissement manuel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Employé</label>
              <Select value={manuel.employee_id || undefined} onValueChange={v => {
                const emp = employees.find(e => e.id === v);
                const firstType = getTypesForEmployee(v)[0]?.value || "retard";
                setManuel(p => ({ ...p, employee_id: v, type: firstType }));
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.prenom} {e.nom}
                      <span className="text-xs text-slate-400 ml-2">
                        ({e.type_employe === 'terrain' ? 'Terrain' : e.type_employe === 'admin' ? 'Admin' : e.type_employe === 'representant' ? 'Représentant' : e.type_employe})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {manuel.employee_id && (() => {
                const emp = employees.find(e => e.id === manuel.employee_id);
                const typesFiltered = getTypesForEmployee(manuel.employee_id);
                return emp ? (
                  <p className="text-xs text-blue-600 mt-1">
                    Rôle: {emp.type_employe || 'terrain'} · {typesFiltered.length} motif{typesFiltered.length > 1 ? 's' : ''} disponible{typesFiltered.length > 1 ? 's' : ''}
                  </p>
                ) : null;
              })()}
            </div>
            <div>
              <label className="text-sm font-medium">Type / Motif</label>
              <Select value={manuel.type} onValueChange={v => setManuel(p => ({ ...p, type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {getTypesForEmployee(manuel.employee_id).map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* 🆕 [Phase 69 — 2026-06-18] Catégorie personnalisée pour "Autre",
                avec autocomplete sur la liste évolutive (datalist HTML5) */}
            {manuel.type === "autre" && (
              <div data-testid="champ-categorie-personnalisee">
                <label className="text-sm font-medium">Nom de la catégorie *</label>
                <input
                  list="categories-perso-suggestions"
                  className="w-full border rounded px-3 py-2 mt-1 text-sm"
                  placeholder="Ex : Mesure disciplinaire, Comportement, etc."
                  value={manuel.categorie_personnalisee || ""}
                  onChange={e => setManuel(p => ({ ...p, categorie_personnalisee: e.target.value }))}
                  data-testid="input-categorie-perso"
                />
                <datalist id="categories-perso-suggestions">
                  {categoriesPerso.map(c => <option key={c} value={c} />)}
                </datalist>
                <p className="text-[11px] text-slate-500 mt-1">
                  Cette étiquette remplacera "Autre" dans l'historique. Les valeurs déjà utilisées sont suggérées automatiquement.
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Date de l'incident</label>
              <input type="date" className="w-full border rounded px-3 py-2 mt-1 text-sm" value={manuel.date_incident} onChange={e => setManuel(p => ({ ...p, date_incident: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea className="mt-1" value={manuel.description} onChange={e => setManuel(p => ({ ...p, description: e.target.value }))} rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium">Notes internes</label>
              <Textarea className="mt-1" value={manuel.notes} onChange={e => setManuel(p => ({ ...p, notes: e.target.value }))} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManuelOpen(false)}>Annuler</Button>
            <Button
              onClick={handleCreerManuel}
              disabled={
                !manuel.employee_id
                || !manuel.description
                // 🆕 [Phase 69] Si type=autre, exiger une catégorie perso saisie
                || (manuel.type === "autre" && !(manuel.categorie_personnalisee || "").trim())
              }
              data-testid="btn-creer-avertissement-manuel"
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Créer l'avertissement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 [Phase 50] Dialog "Comprendre" — source unique de vérité partagée avec /mon-bonus */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-info-avertissements">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500 shrink-0" />
              Comprendre les avertissements
            </DialogTitle>
          </DialogHeader>
          <AvertissementsRichBlock />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionAvertissements;
